import React, { useEffect } from "react";
import { X, Users, Loader2 } from "lucide-react";
import CustomButton from "../ui/CustomButton";
import CurrencyIcon from "../ui/CurrencyIcon";
import SessionCountdown from "./SessionCountdown";
import { DEMO_AMOUNT_LABEL, formatCurrencyAmount, isAmountHidden, resolveDisplayCurrency } from "../../common/currencies";
import { rankMapperSuffix } from "../../utils/rankMapper";

const TournamentConfirmationModal = ({ isOpen, onClose, onConfirm, isConfirming = false, tournament, paymentMethod, displayName }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !tournament) return null;

  const { description = "-", winAmount = 0, entryFee = 0, numPlayers = 0, rankRewards = [], isResumable = false } = tournament;
  // isResume: entering this lobby will resume an in-progress game rather than
  // start a new one, so no entry fee is charged again — reflect that in the copy.
  const isResume = Boolean(isResumable);
  // Game-window expiry (unix ms) for the resume countdown, from GET /lobby.
  const endsAt = tournament?.userActiveSession?.gameWindowEndsAt ?? null;
  // isGem drives the pink (virtual) vs green/black (cash) visual identity.
  const isGem = paymentMethod === "tickets";
  const displayCurrency = resolveDisplayCurrency(tournament.currencyCode);
  const winAmountValue = formatCurrencyAmount(winAmount, displayCurrency);
  const entryFeeValue = formatCurrencyAmount(entryFee, displayCurrency);
  const hideAmounts = isAmountHidden(tournament.currencyCode);
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200 px-4">
      <div
        className={` ${isGem ? 'bg-gradient-to-b from-pinkDarkShade to-pinkLightShade' : 'bg-blackShade2'} border border-white/5 w-full max-w-sm max-h-[90vh] rounded-[32px] shadow-2xl relative animate-in zoom-in-95 duration-200 overflow-hidden flex flex-col`}
        role="dialog"
        aria-modal="true"
      >
        {/* Header background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-24 bg-brand/5 blur-[40px] rounded-full pointer-events-none" />

        {/* Top Text & Close Button */}
        <div className="pt-6 relative px-6 flex items-start justify-between">
          <div className="flex-1 flex justify-center">
            <span className={`text-[10px] font-black ${isGem ? 'text-pinkShade2' : 'text-darkGray'} uppercase tracking-[0.2em] font-inter`}>{displayName || ""}</span>
          </div>
          <button
            onClick={onClose}
            disabled={isConfirming}
            className={`absolute top-4 right-4 w-7 h-7 bg-black rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors border border-white/5 ${isConfirming ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 pt-2 flex flex-col items-center overflow-hidden flex-1 min-h-0">
          {/* Tournament Title */}
          <h2 className="text-[32px] font-black text-center mb-4 uppercase font-saira leading-none">{description?.trim() ? description : "-"}</h2>

          {/* Quick Stats Row */}
          <div className="flex items-center gap-4 text-darkGray  text-[13px] mb-8 font-inter uppercase">
            <div className="flex items-center gap-1.5">
              <span className="text-white flex justify-center items-center gap-1">{hideAmounts ? DEMO_AMOUNT_LABEL.amount : <><CurrencyIcon code={displayCurrency} className="h-4 w-4" />{winAmountValue}</>}</span>
              <span className={`${isGem ? 'text-lightGray' : 'text-darkGray'}`}>Prize Pool</span>
            </div>
            {!hideAmounts && (
              <div className="flex items-center gap-1.5">
                <span className="text-white">{numPlayers}</span>
                <Users className={`w-3.5 h-3.5 ${isGem ? 'text-lightGray' : 'text-darkGray'}`} />
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-white flex justify-center items-center gap-1">{hideAmounts ? DEMO_AMOUNT_LABEL.entry : <><CurrencyIcon code={displayCurrency} className="h-4 w-4" />{entryFeeValue}</>}</span>
              <span className={`${isGem ? 'text-lightGray' : 'text-darkGray'}`}>Entry</span>
            </div>
          </div>

          {/* Prize Table Card */}
          {!hideAmounts && (
            <div className={`w-full ${isGem ? 'bg-pinkDarkShade2' : 'bg-blackShade'} rounded-2xl p-1 px-2 pb-2 mb-8 flex flex-col overflow-hidden min-h-0 shrink`}>
              <div className={`flex items-center justify-between text-[10px] font-black ${isGem ? 'text-lightGray' : 'text-darkGray'} uppercase tracking-widest px-1 shrink-0`}>
                <span>Rank</span>
                <span>Prize</span>
              </div>
              <div className="overflow-y-auto min-h-0">
              {
                (rankRewards && rankRewards.length > 0) ? (
                  rankRewards.map((rankReward, index) => {
                    return <React.Fragment key={index}>
                      <div className={`h-[0.5px] w-full ${isGem ? 'bg-lightGray' : 'bg-darkGray'} `} />
                      <div className="flex items-center justify-between px-1 pt-1">
                        <span className="text-sm font-black font-saira tracking-widest">{rankMapperSuffix(rankReward?.rank)}</span>
                        <span className={`${isGem ? 'text-pinkShade2' : 'text-brand'} text-sm font-black flex justify-center items-center gap-1`}><CurrencyIcon code={displayCurrency} className="h-4 w-4" />{formatCurrencyAmount(rankReward?.rewardAmount, displayCurrency)}</span>
                      </div>
                    </React.Fragment>;
                  })
                ) : (
                  <>
                    <div className={`h-[0.5px] w-full ${isGem ? 'bg-lightGray' : 'bg-darkGray'} `} />
                    <div className="flex items-center justify-between px-1 pt-1">
                      <span className="text-sm font-black font-saira tracking-widest">{rankMapperSuffix(1)}</span>
                      <span className={`${isGem ? 'text-pinkShade2' : 'text-brand'} text-sm font-black flex justify-center items-center gap-1`}><CurrencyIcon code={displayCurrency} className="h-4 w-4" />{winAmountValue}</span>
                    </div>
                  </>
                )
              }
              </div>
            </div>
          )}

          {/* Resume note: continuing an in-progress game does not re-charge entry. */}
          {isResume && (
            <div className="w-full mb-4 rounded-2xl bg-black/30 border border-white/5 px-4 py-3 text-center shrink-0 flex flex-col items-center gap-2">
              {endsAt && <SessionCountdown endsAt={endsAt} />}
              <p className="text-[13px] text-lightGray font-inter">
                It looks like you have a game in progress. Please complete your open match session before it expires.{" "}
                <span className="text-white">No additional entry fees will be charged.</span>
              </p>
            </div>
          )}

          {/* Confirm Button */}
          <CustomButton
            onClick={onConfirm}
            disabled={isConfirming}
            color={isGem ? 'pink' : 'green'}
            className="w-full py-2 font-black font-saira tracking-[0.005em] flex justify-center items-center gap-2 shrink-0"
          >
            {isConfirming ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isResume ? "LOADING" : "FINDING MATCH"}
              </>
            ) : isResume ? (
              "CONTINUE GAME"
            ) : (
              <>
                CONFIRM {hideAmounts ? DEMO_AMOUNT_LABEL.entry : <><CurrencyIcon code={displayCurrency} className="h-4 w-4" />{entryFeeValue}</>} ENTRY
              </>
            )}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default TournamentConfirmationModal;
